// ═══════════════════════════════════════════════════════════════════════════
//  ΚΑΝΟΝΑΣ ΜΟΡΦΟΠΟΙΗΣΗΣ v2 — ΕΝΑ ΣΗΜΕΙΟ ΓΙΑ ΗΜΕΡΟΜΗΝΙΕΣ ΚΑΙ ΠΟΣΑ
//  ---------------------------------------------------------------------------
//  ΠΟΣΑ    : 1.234,56 €   (τελεία=χιλιάδες · κόμμα=δεκαδικά · € μετά, με κενό)
//  ΗΜ/ΝΙΕΣ : 20/04/2027   (βλ. ./dateFormat)
//
//  ⚠️ Η ΕΜΦΑΝΙΣΗ είναι ελληνική· η ΑΠΟΘΗΚΕΥΣΗ μένει αριθμός/ISO όπως ήταν.
//     Ποτέ μη δίνεις formatMoneyEU() σε value={} input ή σε σώμα API —
//     το '1.234,56' δεν είναι έγκυρος αριθμός. Για input χρησιμοποίησε
//     parseMoneyEU() στην ανάγνωση.
// ═══════════════════════════════════════════════════════════════════════════

export * from './dateFormat';

/**
 * 1234.5 → '1.234,50 €'      0 → '0,00 €'      null/NaN → '—'
 * @param v        αριθμός ή αριθμητικό string
 * @param decimals δεκαδικά (προεπιλογή 2· βάλε 0 για στρογγυλά σύνολα)
 * @param symbol   false → χωρίς '€' (για στήλες που έχουν το σύμβολο στην κεφαλίδα)
 */
export function formatMoneyEU(
  v: number | string | null | undefined,
  decimals: number = 2,
  symbol: boolean = true
): string {
  const n = toNumber(v);
  if (n === null) return '—';
  const s = n.toLocaleString('el-GR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return symbol ? `${s} €` : s;
}

/** Σύνολα χωρίς δεκαδικά: 1234.5 → '1.235 €' */
export function formatMoneyEU0(v: number | string | null | undefined): string {
  return formatMoneyEU(v, 0);
}

/**
 * Ό,τι πληκτρολογεί ο χρήστης → αριθμός. Δέχεται ΚΑΙ κόμμα ΚΑΙ τελεία.
 *   '1.234,56' → 1234.56      '1234,56' → 1234.56
 *   '1,234.56' → 1234.56      '1234.56' → 1234.56
 *   '' | σκουπίδια → null
 *
 * ΚΑΝΟΝΑΣ ΔΙΑΚΡΙΣΗΣ: ο ΤΕΛΕΥΤΑΙΟΣ διαχωριστής (', ' ή '.') είναι το δεκαδικό
 * — αν αφήνει πίσω του 1-2 ψηφία. Οτιδήποτε άλλο είναι χιλιάδες και πετιέται.
 */
export function parseMoneyEU(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return isFinite(v) ? v : null;

  let s = String(v).trim().replace(/[€\s ]/g, '');
  if (s === '') return null;

  const neg = /^-/.test(s);
  s = s.replace(/^[-+]/, '');
  if (!/^[\d.,]+$/.test(s)) return null;

  const lastComma = s.lastIndexOf(',');
  const lastDot   = s.lastIndexOf('.');
  const lastSep   = Math.max(lastComma, lastDot);

  /* ═══ ΔΕΚΑΔΙΚΟΣ Ή ΧΙΛΙΑΔΕΣ; ══════════════════════════════════════════════
     ΙΔΙΟΣ ΚΑΝΟΝΑΣ ΜΕ ΤΟΝ SERVER (yms_cents στο api/_payments.php) — δύο
     διαφορετικές απαντήσεις για το ίδιο ποσό σημαίνει οθόνη που δείχνει άλλα
     από όσα χρεώνει η τράπεζα.

     Ο παλιός κανόνας «1-2 ψηφία πίσω = δεκαδικό, αλλιώς χιλιάδες» διάβαζε το
     `6179.196` (τρία δεκαδικά, όπως το γράφει το booking_data) ως 6.179.196.
       · Και κόμμα ΚΑΙ τελεία → ο τελευταίος είναι ο δεκαδικός (1.234,56)
       · Ένας τύπος, πολλές φορές → χιλιάδες (1.234.567)
       · Ένας τύπος, μία φορά: 1-2 πίσω → δεκαδικό · 3 πίσω με ≤3 μπροστά →
         χιλιάδες (6.179) · 3 πίσω με ≥4 μπροστά → δεκαδικό (6179.196) */
  let out: string;
  if (lastSep === -1) {
    out = s;                                   // σκέτα ψηφία
  } else {
    const tail = s.length - lastSep - 1;
    const head = s.slice(0, lastSep);
    const hasComma = lastComma !== -1;
    const hasDot   = lastDot !== -1;
    let isDecimal: boolean;
    if (hasComma && hasDot) {
      isDecimal = true;
    } else {
      const sep = hasComma ? ',' : '.';
      const count = s.split(sep).length - 1;
      isDecimal = count === 1 && tail >= 1
               && (tail !== 3 || head.replace(/\D/g, '').length >= 4);
    }
    out = isDecimal
      ? head.replace(/[.,]/g, '') + '.' + s.slice(lastSep + 1)
      : s.replace(/[.,]/g, '');
  }
  const n = parseFloat(out);
  if (!isFinite(n)) return null;
  return neg ? -n : n;
}

function toNumber(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseMoneyEU(v);
  return n !== null && isFinite(n as number) ? (n as number) : null;
}
